import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Search from "../../../assets/images/search.svg";
import "./PendingPlatesClaims.scss";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import { getPlatesByStatus, deletePlate } from "../../../services/platesService";
import { ConfirmModal } from "../../../components/confirm-modal/ConfirmModal";
import {
  flattenProfilesByPlateStatus,
  getPlateDisplayImageUrl,
  getPlateLocationCountry,
  getPlateLocationState,
  getPlateNumber,
  getProfileDisplayName,
  getProfileEmail,
  getProfileUserImage,
  mapPlateStatusLabel,
} from "../../../utils/platesProfileHelpers";

function PendingPlatesClaims() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [plateToDelete, setPlateToDelete] = useState(null);

  const deletePlateMutation = useMutation({
    mutationFn: (plateId) => deletePlate(plateId),
    onSuccess: (data) => {
      toast.success(data?.message || "Plate deleted successfully", {
        position: "top-right",
        autoClose: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plates-by-status"] });
      queryClient.invalidateQueries({ queryKey: ["plates", "pending"] });
      setPlateToDelete(null);
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete plate";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
    },
  });

  const handleDeleteClick = (row) => setPlateToDelete(row);
  const handleConfirmDelete = () => {
    if (!plateToDelete) return;
    deletePlateMutation.mutate(plateToDelete.id);
  };
  const handleCloseDeleteModal = () => {
    if (!deletePlateMutation.isPending) setPlateToDelete(null);
  };

  // Fetch pending plates
  const {
    data: platesData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["plates", "pending"],
    queryFn: () => getPlatesByStatus("pending"),
    retry: 1,
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch pending plates";
      toast.error(errorMessage);
    },
  });

  // Transform API data: one row per plate entry with status === pending
  const transformedData = useMemo(() => {
    if (!platesData) return [];

    const profiles = platesData.data || platesData.plates || platesData || [];
    const flat = flattenProfilesByPlateStatus(profiles, "pending");

    const formatDate = (dateString) => {
      if (!dateString) return { date: "N/A", time: "" };
      try {
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        const timeStr = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return { date: dateStr, time: timeStr };
      } catch (e) {
        return { date: dateString, time: "" };
      }
    };

    return flat.map(({ profile, plateEntry, plateId }) => {
      const dateInfo = formatDate(
        profile.createdAt || profile.submittedAt || profile.timestamp
      );

      const userName = getProfileDisplayName(profile);
      const userEmail = getProfileEmail(profile);
      const userImage = getProfileUserImage(profile);

      const plateImage =
        getPlateDisplayImageUrl(plateEntry, profile) ||
        process.env.PUBLIC_URL + "/images/uploadedImg.svg";

      const city =
        getPlateLocationState(plateEntry, profile) ||
        profile.location?.city ||
        "N/A";
      const country =
        getPlateLocationCountry(plateEntry, profile) ||
        profile.location?.country ||
        "N/A";

      const plateNumber = getPlateNumber(plateEntry, profile);
      const statusLabel = mapPlateStatusLabel(plateEntry.status);
      const statusClass = (plateEntry.status || "pending").toLowerCase();

      const originalData = { ...profile, plateEntry };

      return {
        id: plateId,
        image: userImage,
        name: userName,
        email: userEmail,
        claimId: plateId,
        plateNumber,
        uploadImg: plateImage,
        datestamp: dateInfo.date,
        timestamp: dateInfo.time,
        city,
        country,
        status: statusLabel,
        statusClass,
        originalData,
      };
    });
  }, [platesData]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return transformedData;

    const searchLower = searchTerm.toLowerCase();
    return transformedData.filter(
      (plate) =>
        plate.name.toLowerCase().includes(searchLower) ||
        plate.email.toLowerCase().includes(searchLower) ||
        plate.plateNumber.toLowerCase().includes(searchLower) ||
        plate.claimId.toLowerCase().includes(searchLower)
    );
  }, [transformedData, searchTerm]);
  const columns = [
    {
      name: "User ID",
      sortable: true,
      grow: 3,
      cell: (row) => (
        <div className="user-cell">
          <img src={row.image} alt="user" className="user-img" />
          <div>
            <strong>{row.name}</strong>
            <br />
            <span>{row.email}</span>
          </div>
        </div>
      ),
    },

    {
      name: "Claim ID",
      selector: (row) => row.claimId,
      sortable: true,
    },

    {
      name: "Plate Number",
      selector: (row) => row.plateNumber,
      sortable: true,
    },
    {
      name: "Uploaded Image",
      sortable: true,
      cell: (row) => (
        <div className="user-cell">
          <div className="upload-img">
            <img src={row.uploadImg} alt="uploadImg" />
          </div>
        </div>
      ),
    },

    {
      name: "Timestamp",
      sortable: true,
      grow: 1.5,
      cell: (row) => (
        <div className="timestamp">
          <strong>{row.datestamp}</strong>
          <br />
          <span>{row.timestamp}</span>
        </div>
      ),
    },

    {
      name: "Location",
      sortable: false,
      cell: (row) => (
        <div className="timestamp">
          <strong>{row.city}</strong>
          <br />
          <span>{row.country}</span>
        </div>
      ),
    },

    {
      name: "Status",
      sortable: true,
      cell: (row) => (
        <span className={`status-badge ${row.statusClass}`}>
          <span className="dot"></span>
          {row.status}
        </span>
      ),
    },

    {
      name: "",
      button: true,
      cell: (row) => (
        <div className="actions">
          <Link
            to="/plates-management/pending-plates-claims/plate-claim-review"
            state={{ plateData: row.originalData, plateId: row.id }}
          >
            <img src={process.env.PUBLIC_URL + "/images/edit.svg"} className="me-2" alt="edit" />
          </Link>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
            disabled={deletePlateMutation.isPending}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
            aria-label="Delete plate"
          >
            <img src={process.env.PUBLIC_URL + "/images/trash.svg"} alt="trash" />
          </button>
        </div>
      ),
    },
  ];
  const customStyles = {
    table: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
      },
    },
    headRow: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      },
    },
    headCells: {
      style: {
        fontSize: "10px",
        fontWeight: "500",
        color: "#0D0D0D",
        padding: "12px 10px",
        backgroundColor: "transparent",
      },
    },
    rows: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
        fontSize: "10px",
        minHeight: "60px",
        "&:hover": {
          backgroundColor: "#EAE3E0 !important",
          border: "none !important",
          outline: "none !important",
        },
      },
    },
  };

  return (
    <React.Fragment>
      <header className="claims-panel-header items-center gap-3 flex content-justify-between">
        <div className="left-side">
          <h4 className="title">Pending Plates Claims</h4>
        </div>
        <div className="right-side">
          <div className="search_bar">
            <img src={Search} alt="Search" />
            <input
              type="text"
              placeholder="Search for pending plates claims..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>
      <div className="pendingPlatesClaims">
        <div className="card">
          <h4>Pending Plates</h4>
          {isLoading ? (
            <div className="loading-container">
              <div className="loader"></div>
              <p>Loading pending plates...</p>
            </div>
          ) : isError ? (
            <div className="error-container">
              <p>Error loading pending plates. Please try again.</p>
              {error && (
                <small>
                  {error?.response?.data?.message || error?.message}
                </small>
              )}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              responsive
              selectableRows
              customStyles={customStyles}
              noDataComponent="No pending plates found"
            />
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!plateToDelete}
        title="Delete plate"
        message={
          plateToDelete
            ? `Are you sure you want to delete plate ${plateToDelete.plateNumber || plateToDelete.id}? This action cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isConfirmLoading={deletePlateMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteModal}
      />
    </React.Fragment>
  );
}

export default PendingPlatesClaims;
